import React, { useCallback, useEffect, useState } from 'react';
import { documentsService } from '../services/documentsService';
import { toast } from '../utils/toast';
import { isPersonalTask } from '../utils/taskKind';
import './WorkActPrompt.css';

const EMPTY = {
  task_id: '', client_name: '', object_name: '', address: '', system_type: '', call_reason: '',
  performed_work: '', findings: '', recommendations: '', technical_state: 'Работоспособно',
  completion_time: '', document_date: '', customer_name: ''
};

export default function WorkActPrompt() {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState('offer');
  const [taskId, setTaskId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadForm = useCallback(async id => {
    setLoading(true); setStage('form');
    try { setForm(await documentsService.getPrefill(id)); }
    catch (error) { toast.error(error.response?.data?.error || 'Не удалось подготовить акт'); setOpen(false); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const onCompleted = event => {
      const task = event.detail?.task;
      if (isPersonalTask(task) || event.detail?.isPersonal === true) return;
      const id = task?.id || event.detail?.taskId;
      if (!id) return;
      setTaskId(id); setStage('offer'); setOpen(true); setForm({ ...EMPTY, task_id: id });
    };
    const onCreate = event => {
      const id = event.detail?.taskId;
      if (!id) return;
      setTaskId(id); setOpen(true); setForm({ ...EMPTY, task_id: id });
      void loadForm(id);
    };
    window.addEventListener('crm:task-completed', onCompleted);
    window.addEventListener('crm:create-work-act', onCreate);
    return () => {
      window.removeEventListener('crm:task-completed', onCompleted);
      window.removeEventListener('crm:create-work-act', onCreate);
    };
  }, [loadForm]);

  const close = () => { setOpen(false); setTaskId(null); setStage('offer'); setForm(EMPTY); };
  const set = (key, value) => setForm(previous => ({ ...previous, [key]: value }));

  const submit = async event => {
    event.preventDefault(); setSaving(true);
    try {
      const document = await documentsService.createWorkAct(form);
      toast.success(`Акт №${document.document_number} создан`);
      try { await documentsService.download(document.id, document.file_name); }
      catch (_) { toast.error('Акт сохранён в «Документах», но автоматическое скачивание не началось'); }
      window.dispatchEvent(new CustomEvent('crm:document-created', { detail: { document } }));
      close();
    } catch (error) { toast.error(error.response?.data?.error || 'Не удалось создать акт'); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  if (stage === 'offer') return <div className="act-modal-backdrop"><div className="act-offer" role="dialog" aria-modal="true"><div className="act-offer-icon">📄</div><span className="act-kicker">ЗАДАЧА ЗАВЕРШЕНА</span><h2>Создать акт выполненных работ?</h2><p>Необязательно. Если акт нужен — основные данные заявки подставятся автоматически, останется описать результат.</p><div className="act-offer-actions"><button className="act-secondary" onClick={close}>Не сейчас</button><button className="act-primary" onClick={() => loadForm(taskId)}>Создать акт</button></div></div></div>;

  return <div className="act-modal-backdrop"><form className="act-form-modal" onSubmit={submit} role="dialog" aria-modal="true"><header><div><span className="act-kicker">АКТ ВЫПОЛНЕННЫХ РАБОТ</span><h2>Заполните результат выезда</h2><p>Название объекта, адрес и дата уже взяты из заявки.</p></div><button type="button" className="act-close" onClick={close}>✕</button></header>
    {loading ? <div className="act-loading">Подготавливаю данные…</div> : <>
      <div className="act-form-grid">
        <label>Дата акта<input type="date" required value={form.document_date || ''} onChange={e => set('document_date', e.target.value)} /></label>
        <label>Окончание работ<input type="time" value={form.completion_time || ''} onChange={e => set('completion_time', e.target.value)} /></label>
        <label>Клиент<input value={form.client_name || ''} onChange={e => set('client_name', e.target.value)} /></label>
        <label>Название объекта<input required value={form.object_name || ''} onChange={e => set('object_name', e.target.value)} /></label>
        <label className="wide">Адрес<input value={form.address || ''} onChange={e => set('address', e.target.value)} /></label>
        <label>Тип системы<input value={form.system_type || ''} onChange={e => set('system_type', e.target.value)} placeholder="СКУД, видеонаблюдение…" /></label>
        <label>Техническое состояние<select value={form.technical_state || ''} onChange={e => set('technical_state', e.target.value)}><option>Работоспособно</option><option>Работоспособно, требуется наблюдение</option><option>Частично работоспособно</option><option>Неисправно</option></select></label>
        <label className="wide">Причина вызова<textarea rows="2" value={form.call_reason || ''} onChange={e => set('call_reason', e.target.value)} /></label>
        <label className="wide important">Что именно сделано <span>*</span><textarea autoFocus required rows="4" value={form.performed_work || ''} onChange={e => set('performed_work', e.target.value)} placeholder="Проверил контроллер, восстановил линию, настроил считыватель…" /></label>
        <label className="wide">Что выявлено<textarea rows="3" value={form.findings || ''} onChange={e => set('findings', e.target.value)} placeholder="Если замечаний нет — можно оставить пустым" /></label>
        <label className="wide">Что требуется в дальнейшем<textarea rows="3" value={form.recommendations || ''} onChange={e => set('recommendations', e.target.value)} placeholder="Что заменить, проверить или запланировать следующим выездом" /></label>
        <label className="wide">Представитель заказчика<input value={form.customer_name || ''} onChange={e => set('customer_name', e.target.value)} placeholder="Можно оставить только название клиента" /></label>
      </div>
      <footer><button type="button" className="act-secondary" onClick={close}>Отмена</button><button className="act-primary" disabled={saving}>{saving ? 'Создаю документ…' : 'Создать и скачать DOCX'}</button></footer>
    </>}
  </form></div>;
}
